import { Head, router } from "@inertiajs/react"
import type { ColumnDef } from "@tanstack/react-table"
import { Pencil, Trash2, VenetianMask } from "lucide-react"

import TextLink from "@/components/text-link"
import { Badge } from "@/components/ui/badge"
import { DataTable } from "@/components/ui/data-table"
import AdminLayout from "@/layouts/admin-layout"
import {
  adminPartnerPath,
  adminPartnerUserPath,
  adminPartnerUsersPath,
  editAdminPartnerUserPath,
  impersonateAdminPartnerUserPath,
} from "@/routes"
import type { BreadcrumbItem, Pagy } from "@/types"

interface PartnerUser {
  uid: string
  name: string
  email: string
  role: string
  created_at: string
  last_sign_in_at: string | null
  invitation_accepted_at: string | null
  partner: { name: string; uid: string } | null
}

const columns: ColumnDef<PartnerUser>[] = [
  {
    accessorKey: "name",
    enableSorting: true,
    header: "Name/Email",
    cell: ({ row }) => (
      <TextLink
        href={adminPartnerUserPath({ id: row.original.uid })}
        as="div"
        prefetch
      >
        <div className="font-medium">{row.original.name}</div>
        <div className="text-muted-foreground text-sm">
          {row.original.email}
        </div>
      </TextLink>
    ),
  },
  {
    id: "partner",
    header: "Partner",
    cell: ({ row }) =>
      row.original.partner ? (
        <TextLink
          href={adminPartnerPath({ id: row.original.partner.uid })}
          as="div"
          prefetch
        >
          {row.original.partner.name}
        </TextLink>
      ) : (
        "-"
      ),
  },
  {
    accessorKey: "last_sign_in_at",
    enableSorting: true,
    header: "Last Login",
    cell: ({ row }) =>
      row.original.last_sign_in_at
        ? new Date(row.original.last_sign_in_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "-",
  },
  {
    accessorKey: "created_at",
    enableSorting: true,
    header: "Created",
    cell: ({ row }) =>
      new Date(row.original.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => (
      <Badge
        variant={row.original.invitation_accepted_at ? "operational" : "draft"}
      >
        {row.original.invitation_accepted_at ? "Active" : "Pending"}
      </Badge>
    ),
  },
]

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Partner Users", href: adminPartnerUsersPath() },
]

export default function PartnerUsersIndex({
  partner_users,
  pagy,
  sort,
}: {
  partner_users: PartnerUser[]
  pagy: Pagy
  sort: string | null
}) {
  return (
    <AdminLayout breadcrumbs={breadcrumbs}>
      <Head title="Partner Users" />
      <DataTable
        columns={columns}
        data={partner_users}
        pagy={pagy}
        sort={sort}
        onSortChange={(newSort) => {
          router.visit(adminPartnerUsersPath(), {
            data: { sort: newSort },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        onPageChange={(page) => {
          router.visit(adminPartnerUsersPath(), {
            data: { page, sort: sort ?? undefined },
            preserveState: true,
            preserveScroll: true,
          })
        }}
        actions={[
          {
            label: "Edit",
            icon: <Pencil className="h-4 w-4" />,
            onClick: (partnerUser: PartnerUser) =>
              router.visit(editAdminPartnerUserPath({ id: partnerUser.uid })),
          },
          {
            label: "Impersonate",
            icon: <VenetianMask className="h-4 w-4" />,
            onClick: (partnerUser: PartnerUser) =>
              router.post(
                impersonateAdminPartnerUserPath({ id: partnerUser.uid }),
              ),
            // Same gate as the detail page: no usable session to impersonate
            // until the invitation is accepted.
            visible: (partnerUser: PartnerUser) =>
              !!partnerUser.invitation_accepted_at,
          },
          {
            label: "Delete",
            icon: <Trash2 className="h-4 w-4" />,
            onClick: (partnerUser: PartnerUser) => {
              router.delete(adminPartnerUserPath({ id: partnerUser.uid }), {
                preserveState: true,
                preserveScroll: true,
              })
            },
            variant: "destructive",
            visible: (partnerUser: PartnerUser) =>
              !partnerUser.invitation_accepted_at,
          },
        ]}
      />
    </AdminLayout>
  )
}
